import React from "react";
import { classMerge, EditorDefns, isString, type EditorProps } from "../../utility";
import { ArsTableCellData, ArsTableCellType, ArsTextJustification } from "../table";
import { isObjectRecord } from "../typeGuards";
import styles from "./ArsListing.module.css";
import { useArsListing } from "./ArsListingContextProvider";
import { ARSL_CELL_UPDATE, HEADER_ROW_ID } from "./interface";

export interface ArsListingCellProps<T = string> extends EditorProps<ArsTableCellData<T>> {
  rowId?: string;
  colId?: string;
}

/**
 * Lightweight read-only cell renderer used by scaffold definitions.
 *
 * @param props - Scaffold editor props
 * @param props.id - Unique scaffold-generated cell identifier
 * @param props.colId - Column identifier passed by ScaffoldCell
 * @param props.rowId - Row identifier passed by ScaffoldCell
 * @param props.setValue - Scaffold-provided value setter (not used in read-only cell)
 * @param props.value - Cell value from scaffold cell data
 * @returns A span showing the cell value with text alignment applied
 */
export const ArsListingCell = <T,>({
  id,
  colId,
  rowId,
  value,
}: ArsListingCellProps<T>): React.ReactElement => {
  const { state, dispatch } = useArsListing<T>();
  if (!state || !colId || !rowId) return <></>;

  const isHeaderRow = rowId === HEADER_ROW_ID;
  const column = state.bodyData.arsListingColumns.find((col) => col.id === colId);
  if (!column) return <></>;

  const textJustification: ArsTextJustification = value.justification;

  const editorProps = state.settings.editorProps;
  const Editor = editorProps.Editor;
  const isAnnotationsView = state.settings.tlfViewMode.value === "ANNOTATIONS";

  const displayValue = value.displayContent;
  const annotationValue = value.annotationContent;
  const disabled =
    state.settings.disabled ||
    value.disabled ||
    (state.settings.tlfViewMode.value === "ANNOTATIONS" && isHeaderRow) ||
    state.bodyData.arsBodyRows.find((row) => row.id === rowId)?.ellipsis;

  return (
    <div
      style={{
        paddingLeft: value.inlineIndent ? `${value.inlineIndent}em` : undefined,
      }}
      className={classMerge([styles.cell, disabled ? styles.disabled : ""])}
    >
      <Editor
        id={id}
        decimalAlignPercent={column.decimalAlignPercent}
        editable={!disabled}
        pending={state.changed}
        setValue={(newValue: T) => {
          dispatch({
            operation: ARSL_CELL_UPDATE,
            rowId,
            colId,
            value: newValue,
          });
        }}
        textAlignment={textJustification}
        value={
          isAnnotationsView
            ? editorProps.textToT(annotationValue)
            : (displayValue ??
              // istanbul ignore next
              editorProps.blankT)
        }
      />
    </div>
  );
};

ArsListingCell.displayName = "ArsListingCell";

/**
 * Create editor props for use with ArsTableCell
 *
 * @param tProps - Editor definitions for the generic type T used in the cell's displayContent and annotationContent
 * @returns - Editor definitions for ArsListingCell with cell data as the generic type
 */
export const ArsListingCellEditorProps = <T,>(
  tProps: EditorDefns<T>,
): EditorDefns<ArsTableCellData<T>> => ({
  Editor: ArsListingCell,
  blankT: {
    annotationContent: "",
    cellType: ArsTableCellType.DataCell,
    columnId: "",
    columnIndex: -1,
    dataPath: "",
    displayContent: tProps.textToT(""),
    justification: ArsTextJustification.Default,
    rowId: "",
    targetType: "data",
  },
  getTextFromT: (cellValue: ArsTableCellData<T>) => {
    const cellDisplay = cellValue.displayContent;
    return tProps.getTextFromT(cellDisplay);
  },
  isT: (value: unknown): value is ArsTableCellData<T> => {
    return (
      isObjectRecord(value) &&
      "annotationContent" in value &&
      isString(value.annotationContent) &&
      "cellType" in value &&
      Object.values(ArsTableCellType).includes(value.cellType as ArsTableCellType) &&
      "dataPath" in value &&
      isString(value.dataPath) &&
      "displayContent" in value &&
      tProps.isT(value.displayContent)
    );
  },
  joinTintoBlock: (cellValues: ArsTableCellData<T>[]) => {
    const displayContents = cellValues.map((cv) => cv.displayContent);
    return {
      annotationContent: cellValues.map((cv) => cv.annotationContent).join("\n"),
      cellType: ArsTableCellType.DataCell,
      columnId:
        cellValues[0]?.columnId ??
        // istanbul ignore next
        "",
      columnIndex:
        cellValues[0]?.columnIndex ??
        // istanbul ignore next
        -1,
      dataPath:
        cellValues[0]?.dataPath ??
        // istanbul ignore next
        "",
      displayContent: tProps.joinTintoBlock(displayContents),
      justification:
        cellValues[0]?.justification ??
        // istanbul ignore next
        ArsTextJustification.Default,
      rowId:
        cellValues[0]?.rowId ??
        // istanbul ignore next
        "",
      targetType: "data",
    };
  },
  replaceTextInT: (
    cellValue: ArsTableCellData<T>,
    oldPhrase: string,
    newPhrase: ArsTableCellData<T>,
  ) => {
    return {
      ...cellValue,
      displayContent: tProps.replaceTextInT(
        cellValue.displayContent,
        oldPhrase,
        newPhrase.displayContent,
      ),
    };
  },
  splitTintoLines: (cellValue: ArsTableCellData<T>) => {
    const displayContent = cellValue.displayContent;
    const splitDisplay = tProps.splitTintoLines(displayContent);
    return splitDisplay.map((line) => ({
      annotationContent: cellValue.annotationContent,
      cellType: ArsTableCellType.DataCell,
      columnId: cellValue.columnId,
      columnIndex: cellValue.columnIndex,
      dataPath: cellValue.dataPath,
      displayContent: line,
      justification: cellValue.justification,
      rowId: cellValue.rowId,
      targetType: "data",
    }));
  },
  textToT: (text: string) => ({
    annotationContent: "",
    cellType: ArsTableCellType.DataCell,
    columnId: "",
    columnIndex: -1,
    dataPath: "",
    displayContent: tProps.textToT(text),
    rowId: "",
    justification: ArsTextJustification.Default,
    targetType: "data",
  }),
});
